export type HttpOptions = {
  headers?: Record<string, string>
  method?: string
}

export type HttpUploadOptions = HttpOptions & {
  url: string
  // eslint-disable-next-line @typescript-eslint/ban-types
  body: object
  onProgress?: (progress: number) => void
}

export class HttpUploadError extends Error {
  constructor(description: string) {
    super(description)
    this.name = 'HttpUploadError'
  }
}

const responseFromXhr = (xhr: XMLHttpRequest) =>
  new Response(xhr.responseText, {
    status: xhr.status,
    statusText: xhr.statusText,
    headers: xhr
      .getAllResponseHeaders()
      .trim()
      .split(/[\r\n]+/)
      .map(line => line.split(': '))
      .reduce((headers, [key, value]) => {
        headers.append(key, value)
        return headers
      }, new Headers()),
  })

export const upload = ({
  url,
  body,
  headers,
  method = 'POST',
  onProgress,
}: HttpUploadOptions): Promise<Response> =>
  new Promise((resolve, reject) => {
    const urlAsString = url.toString()
    const methodUppercase = method.toUpperCase()
    const xhr = new XMLHttpRequest()
    const form = new FormData()
    const rejectWithUploadErrorWhileUploading = () =>
      reject(new HttpUploadError(`${methodUppercase} ${url} failed during upload.`))
    const rejectWithUploadErrorWhileDownloading = () =>
      reject(
        new HttpUploadError(
          `${methodUppercase} ${url} failed after upload while downloading the response.`
        )
      )

    if (onProgress) {
      xhr.upload.addEventListener('progress', event => {
        if (event.lengthComputable && onProgress) {
          onProgress(event.loaded / event.total)
        }
      })
    }

    xhr.upload.addEventListener('error', rejectWithUploadErrorWhileUploading)
    xhr.upload.addEventListener('abort', rejectWithUploadErrorWhileUploading)
    xhr.addEventListener('error', rejectWithUploadErrorWhileDownloading)
    xhr.addEventListener('abort', rejectWithUploadErrorWhileDownloading)
    xhr.addEventListener('load', () => resolve(responseFromXhr(xhr)))

    xhr.open(method.toUpperCase(), urlAsString)

    if (headers) {
      Object.entries(headers).forEach(([header, value]) => {
        xhr.setRequestHeader(header, value)
      })
    }

    Object.entries(body).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach(v => form.append(key, v))
      } else {
        form.append(key, value)
      }
    })
    xhr.send(form)
  })
